import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useRedEstado } from "@/lib/red";
import { Section } from "@/components/common/DetailFields";
import { FounderBadge } from "@/components/red/FounderBadge";

const Field = ({ label, hint, children }) => <label className="block space-y-1"><span className="text-xs c-text-2">{label}</span>{children}{hint && <span className="block text-[11px] c-muted">{hint}</span>}</label>;

const Status = ({ e }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs">
    <FounderBadge estado={e} testid="config-founder-badge" />
    {e.es_admin && <span data-testid="red-admin-badge" className="px-2 py-0.5 rounded-full border c-line font-semibold">Admin</span>}
    <span data-testid="red-activation-status" className="c-text-2">{e.activado ? "Red desbloqueada" : `Red bloqueada · ${e.conteo} de ${e.meta}`}</span>
    <span className="c-muted">· Código: <span data-testid="red-invite-code" className="font-mono font-semibold">{e.codigo_invitacion}</span></span>
  </div>
);

export const RedProfileSection = () => {
  const [e, , setE] = useRedEstado();
  const [f, setF] = useState({ nombre_publico: "", ciudad: "", telefono: "", telefono_privado: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (e) setF({ nombre_publico: e.nombre_publico || "", ciudad: e.ciudad || "", telefono: "", telefono_privado: e.telefono_privado || "" }); }, [e]);
  if (!e) return null;
  const set = (k) => (ev) => setF((s) => ({ ...s, [k]: ev.target.value }));
  const save = async () => {
    const payload = { nombre_publico: f.nombre_publico.trim(), ciudad: f.ciudad.trim() };
    if (f.telefono.trim()) payload.telefono = f.telefono.trim();
    if (f.telefono_privado.trim() !== (e.telefono_privado || "")) payload.telefono_privado = f.telefono_privado.trim();
    setBusy(true);
    try { setE((await api.patch("/red/perfil", payload)).data); toast.success("Perfil de red guardado"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Section title="Red CONECTA" testid="config-red-profile">
      <div className="space-y-3">
        <Status e={e} />
        {e.avalado_por && <p data-testid="config-avalado" className="text-xs c-text-2">Avalado por <Link to={`/conecta/u/${e.avalado_por.id}`} className="font-semibold c-accent">{e.avalado_por.nombre_publico}</Link></p>}
        <p className="text-xs c-muted">Gratis para fundadores.</p>
        {e.puede_editar ? (
          <>
            <Field label="Nombre público"><input data-testid="red-public-name-input" className="c-input" value={f.nombre_publico} onChange={set("nombre_publico")} /></Field>
            <Field label="Ciudad"><input data-testid="red-city-input" className="c-input" value={f.ciudad} onChange={set("ciudad")} /></Field>
            <Field label="Teléfono (WhatsApp)" hint={e.tiene_telefono ? "Ya registraste un teléfono. Solo guardamos una huella cifrada, nunca el número." : "Sirve para que tus conocidos te encuentren. Solo guardamos una huella cifrada (+52 por defecto)."}>
              <input data-testid="red-phone-input" type="tel" inputMode="tel" className="c-input" placeholder={e.tiene_telefono ? "•••• registrado · escribe para cambiarlo" : "55 1234 5678"} value={f.telefono} onChange={set("telefono")} />
            </Field>
            <Field label="Teléfono de contacto privado" hint="Se precarga cuando pides una presentación. Nunca sale en búsquedas ni en tu perfil público: solo lo ve quien recibe tu solicitud.">
              <input data-testid="red-private-phone-input" type="tel" inputMode="tel" className="c-input" placeholder="55 1234 5678" value={f.telefono_privado} onChange={set("telefono_privado")} />
            </Field>
            {e.tiene_telefono && <p data-testid="red-phone-registered" className="text-xs c-text-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 c-accent" /> Teléfono registrado</p>}
            <button data-testid="red-profile-save-btn" disabled={busy || f.nombre_publico.trim().length < 2 || f.ciudad.trim().length < 2} onClick={save} className="c-btn-primary w-full py-3 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar perfil de red
            </button>
          </>
        ) : (
          <p className="text-sm c-text-2">Actúas en la red a nombre de <span className="font-semibold">{e.nombre_publico}</span>.</p>
        )}
      </div>
    </Section>
  );
};
